
/**
 * Catalogue entries for the "Engineering" category. Authored here: this catalogue is the source of
 * truth for the function metadata API, and `docs/guide/built-in-functions.md` is generated from it.
 */
export const ENGINEERING_DOCS = {
    BESSELI: {
        category: 'Engineering',
        shortDescription: 'Returns the value of the modified Bessel function of the first kind, In(x).',
        parameters: [{ name: 'x', description: 'The value at which the modified Bessel function is evaluated.' }, { name: 'n', description: 'The order of the Bessel function; a non-negative integer.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BESSELI(1.5, 1)'],
    },
    BESSELJ: {
        category: 'Engineering',
        shortDescription: 'Returns the value of the Bessel function of the first kind, Jn(x).',
        parameters: [{ name: 'x', description: 'The value at which the Bessel function is evaluated.' }, { name: 'n', description: 'The order of the Bessel function; a non-negative integer.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BESSELJ(1.9, 2)'],
    },
    BESSELK: {
        category: 'Engineering',
        shortDescription: 'Returns the value of the modified Bessel function of the second kind, Kn(x).',
        parameters: [{ name: 'x', description: 'The value at which the modified Bessel function is evaluated; must be greater than 0.' }, { name: 'n', description: 'The order of the Bessel function; a non-negative integer.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BESSELK(1.5, 1)'],
    },
    BESSELY: {
        category: 'Engineering',
        shortDescription: 'Returns the value of the Bessel function of the second kind, Yn(x).',
        parameters: [{ name: 'x', description: 'The value at which the Bessel function is evaluated; must be greater than 0.' }, { name: 'n', description: 'The order of the Bessel function; a non-negative integer.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BESSELY(1.5, 1)'],
    },
    BIN2DEC: {
        category: 'Engineering',
        shortDescription: 'The result is the decimal number for the binary number entered.',
        parameters: [{ name: 'number', description: 'The binary number (as text, up to 10 digits) to convert to decimal.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BIN2DEC(1100100)', '=BIN2DEC(111)'],
    },
    BIN2HEX: {
        category: 'Engineering',
        shortDescription: 'The result is the hexadecimal number for the binary number entered.',
        parameters: [{ name: 'number', description: 'The binary number (as text, up to 10 digits) to convert to hexadecimal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BIN2HEX(1100100)', '=BIN2HEX(1100100, 4)'],
    },
    BIN2OCT: {
        category: 'Engineering',
        shortDescription: 'The result is the octal number for the binary number entered.',
        parameters: [{ name: 'number', description: 'The binary number (as text, up to 10 digits) to convert to octal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BIN2OCT(1100100)', '=BIN2OCT(1100100, 4)'],
    },
    BITAND: {
        category: 'Engineering',
        shortDescription: 'Returns a bitwise logical "and" of the parameters.',
        parameters: [{ name: 'number1', description: 'The first non-negative integer to combine with a bitwise AND.' }, { name: 'number2', description: 'The second non-negative integer to combine with a bitwise AND.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BITAND(5, 3)', '=BITAND(12, 10)'],
    },
    BITLSHIFT: {
        category: 'Engineering',
        shortDescription: 'Shifts a number left by n bits.',
        parameters: [{ name: 'number', description: 'The non-negative integer whose bits are shifted.' }, { name: 'shift', description: 'The number of bits to shift left by; a negative value shifts right instead.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BITLSHIFT(4, 2)', '=BITLSHIFT(1, 5)'],
    },
    BITOR: {
        category: 'Engineering',
        shortDescription: 'Returns a bitwise logical "or" of the parameters.',
        parameters: [{ name: 'number1', description: 'The first non-negative integer to combine with a bitwise OR.' }, { name: 'number2', description: 'The second non-negative integer to combine with a bitwise OR.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BITOR(5, 3)', '=BITOR(8, 4)'],
    },
    BITRSHIFT: {
        category: 'Engineering',
        shortDescription: 'Shifts a number right by n bits.',
        parameters: [{ name: 'number', description: 'The non-negative integer whose bits are shifted.' }, { name: 'shift', description: 'The number of bits to shift right by; a negative value shifts left instead.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BITRSHIFT(16, 2)', '=BITRSHIFT(8, 1)'],
    },
    BITXOR: {
        category: 'Engineering',
        shortDescription: 'Returns a bitwise logical "exclusive or" of the parameters.',
        parameters: [{ name: 'number1', description: 'The first non-negative integer to combine with a bitwise exclusive OR.' }, { name: 'number2', description: 'The second non-negative integer to combine with a bitwise exclusive OR.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=BITXOR(5, 3)', '=BITXOR(12, 10)'],
    },
    COMPLEX: {
        category: 'Engineering',
        shortDescription: 'Returns a complex number built from its real and imaginary parts.',
        parameters: [{ name: 're', description: 'The real coefficient of the complex number.' }, { name: 'im', description: 'The imaginary coefficient of the complex number.' }, { name: 'symbol', description: 'The suffix used for the imaginary unit, either "i" or "j"; defaults to "i" when omitted.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=COMPLEX(3, 4)', '=COMPLEX(2, -1, "j")'],
    },
    DEC2BIN: {
        category: 'Engineering',
        shortDescription: 'Returns the binary number for the decimal number entered between –512 and 511.',
        parameters: [{ name: 'number', description: 'The decimal integer, between -512 and 511, to convert to binary.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=DEC2BIN(10)', '=DEC2BIN(10, 8)'],
    },
    DEC2HEX: {
        category: 'Engineering',
        shortDescription: 'Returns the hexadecimal number for the decimal number entered.',
        parameters: [{ name: 'number', description: 'The decimal integer to convert to hexadecimal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=DEC2HEX(100)', '=DEC2HEX(100, 4)'],
    },
    DEC2OCT: {
        category: 'Engineering',
        shortDescription: 'Returns the octal number for the decimal number entered.',
        parameters: [{ name: 'number', description: 'The decimal integer to convert to octal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=DEC2OCT(58)', '=DEC2OCT(58, 4)'],
    },
    DELTA: {
        category: 'Engineering',
        shortDescription: 'Returns TRUE (1) if both numbers are equal, otherwise returns FALSE (0).',
        parameters: [{ name: 'number1', description: 'The first number to compare.' }, { name: 'number2', description: 'The second number to compare; defaults to 0 when omitted.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=DELTA(5, 5)', '=DELTA(5, 4)', '=DELTA(0)'],
    },
    ERF: {
        category: 'Engineering',
        shortDescription: 'Returns values of the Gaussian error integral.',
        parameters: [{ name: 'lower_limit', description: 'The lower bound of the integral, or the single integration limit from 0 when upper_limit is omitted.' }, { name: 'upper_limit', description: 'The upper bound of the integral; when omitted, the integral is calculated from 0 to lower_limit.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=ERF(1)', '=ERF(0, 1)'],
    },
    ERFC: {
        category: 'Engineering',
        shortDescription: 'Returns complementary values of the Gaussian error integral between x and infinity.',
        parameters: [{ name: 'lower_limit', description: 'The lower bound of the integral, calculated from this value to infinity.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=ERFC(1)', '=ERFC(0.5)'],
    },
    HEX2BIN: {
        category: 'Engineering',
        shortDescription: 'The result is the binary number for the hexadecimal number entered.',
        parameters: [{ name: 'number', description: 'The hexadecimal number (as text, up to 10 digits) to convert to binary.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=HEX2BIN("2A")', '=HEX2BIN("2A", 8)'],
    },
    HEX2DEC: {
        category: 'Engineering',
        shortDescription: 'The result is the decimal number for the hexadecimal number entered.',
        parameters: [{ name: 'number', description: 'The hexadecimal number (as text, up to 10 digits) to convert to decimal.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=HEX2DEC("2A")', '=HEX2DEC("FF")'],
    },
    HEX2OCT: {
        category: 'Engineering',
        shortDescription: 'The result is the octal number for the hexadecimal number entered.',
        parameters: [{ name: 'number', description: 'The hexadecimal number (as text, up to 10 digits) to convert to octal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=HEX2OCT("2A")', '=HEX2OCT("2A", 4)'],
    },
    IMABS: {
        category: 'Engineering',
        shortDescription: 'Returns modulus of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose modulus is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMABS("3+4i")', '=IMABS("-i")'],
    },
    IMAGINARY: {
        category: 'Engineering',
        shortDescription: 'Returns imaginary part of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose imaginary coefficient is returned.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMAGINARY("3+4i")'],
    },
    IMARGUMENT: {
        category: 'Engineering',
        shortDescription: 'Returns argument of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose argument (angle in radians) is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMARGUMENT("3+4i")'],
    },
    IMCONJUGATE: {
        category: 'Engineering',
        shortDescription: 'Returns conjugate of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose complex conjugate is returned.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCONJUGATE("3+4i")'],
    },
    IMCOS: {
        category: 'Engineering',
        shortDescription: 'Returns cosine of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose cosine is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCOS("3+4i")'],
    },
    IMCOSH: {
        category: 'Engineering',
        shortDescription: 'Returns hyperbolic cosine of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose hyperbolic cosine is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCOSH("3+4i")'],
    },
    IMCOT: {
        category: 'Engineering',
        shortDescription: 'Returns cotangent of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose cotangent is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCOT("3+4i")'],
    },
    IMCSC: {
        category: 'Engineering',
        shortDescription: 'Returns cosecant of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose cosecant is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCSC("3+4i")'],
    },
    IMCSCH: {
        category: 'Engineering',
        shortDescription: 'Returns hyperbolic cosecant of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose hyperbolic cosecant is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMCSCH("3+4i")'],
    },
    IMDIV: {
        category: 'Engineering',
        shortDescription: 'Divides two complex numbers.',
        parameters: [{ name: 'complex1', description: 'The complex number to divide (dividend), given as text such as "3+4i".' }, { name: 'complex2', description: 'The complex number to divide by (divisor), given as text such as "1+2i".' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMDIV("3+4i", "1+2i")'],
    },
    IMEXP: {
        category: 'Engineering',
        shortDescription: 'Returns exponent of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose exponential is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMEXP("3+4i")'],
    },
    IMLN: {
        category: 'Engineering',
        shortDescription: 'Returns natural logarithm of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose natural logarithm is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMLN("3+4i")'],
    },
    IMLOG10: {
        category: 'Engineering',
        shortDescription: 'Returns base-10 logarithm of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose base-10 logarithm is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMLOG10("3+4i")'],
    },
    IMLOG2: {
        category: 'Engineering',
        shortDescription: 'Returns binary logarithm of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose base-2 logarithm is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMLOG2("3+4i")'],
    },
    IMPOWER: {
        category: 'Engineering',
        shortDescription: 'Returns a complex number raised to a given power.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", to raise to a power.' }, { name: 'number', description: 'The exponent to raise the complex number to.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMPOWER("3+4i", 2)'],
    },
    IMPRODUCT: {
        category: 'Engineering',
        shortDescription: 'Multiplies complex numbers.',
        parameters: [{ name: 'complex1', description: 'A complex number, cell, or range to multiply. Further complex numbers or ranges can be passed as additional arguments.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMPRODUCT("3+4i", "1-2i")', '=IMPRODUCT(A1:A5)'],
    },
    IMREAL: {
        category: 'Engineering',
        shortDescription: 'Returns real part of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose real coefficient is returned.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMREAL("3+4i")'],
    },
    IMSEC: {
        category: 'Engineering',
        shortDescription: 'Returns the secant of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose secant is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSEC("3+4i")'],
    },
    IMSECH: {
        category: 'Engineering',
        shortDescription: 'Returns the hyperbolic secant of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose hyperbolic secant is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSECH("3+4i")'],
    },
    IMSIN: {
        category: 'Engineering',
        shortDescription: 'Returns sine of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose sine is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSIN("3+4i")'],
    },
    IMSINH: {
        category: 'Engineering',
        shortDescription: 'Returns hyperbolic sine of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose hyperbolic sine is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSINH("3+4i")'],
    },
    IMSQRT: {
        category: 'Engineering',
        shortDescription: 'Returns a square root of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose square root is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSQRT("3+4i")', '=IMSQRT("-1")'],
    },
    IMSUB: {
        category: 'Engineering',
        shortDescription: 'Subtracts two complex numbers.',
        parameters: [{ name: 'complex1', description: 'The complex number to subtract from, given as text such as "3+4i".' }, { name: 'complex2', description: 'The complex number to subtract, given as text such as "1+2i".' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSUB("3+4i", "1+2i")'],
    },
    IMSUM: {
        category: 'Engineering',
        shortDescription: 'Adds complex numbers.',
        parameters: [{ name: 'complex1', description: 'A complex number, cell, or range to add. Further complex numbers or ranges can be passed as additional arguments.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMSUM("3+4i", "1-2i")', '=IMSUM(A1:A5)'],
    },
    IMTAN: {
        category: 'Engineering',
        shortDescription: 'Returns the tangent of a complex number.',
        parameters: [{ name: 'complex', description: 'The complex number, given as text such as "3+4i", whose tangent is calculated.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=IMTAN("3+4i")'],
    },
    OCT2BIN: {
        category: 'Engineering',
        shortDescription: 'The result is the binary number for the octal number entered.',
        parameters: [{ name: 'number', description: 'The octal number (as text, up to 10 digits) to convert to binary.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=OCT2BIN(14)', '=OCT2BIN(14, 8)'],
    },
    OCT2DEC: {
        category: 'Engineering',
        shortDescription: 'The result is the decimal number for the octal number entered.',
        parameters: [{ name: 'number', description: 'The octal number (as text, up to 10 digits) to convert to decimal.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=OCT2DEC(14)', '=OCT2DEC(17)'],
    },
    OCT2HEX: {
        category: 'Engineering',
        shortDescription: 'The result is the hexadecimal number for the octal number entered.',
        parameters: [{ name: 'number', description: 'The octal number (as text, up to 10 digits) to convert to hexadecimal.' }, { name: 'places', description: 'The number of digits the result is padded to with leading zeros; a #NUM! error when the result needs more digits than that. Ignored for negative numbers. If omitted, no padding is applied.' }],
        documentationUrl: 'https://hyperformula.handsontable.com/docs/guide/built-in-functions.html',
        examples: ['=OCT2HEX(14)', '=OCT2HEX(14, 4)'],
    },
};
